import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex, GlobalToken, Typography } from "antd";
import styled from "styled-components";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { Button, Eyebrow, Skeleton, radius } from "@libertify/ui";
import bkgNoise from "@/assets/bkg-noise-grey.png";
import { useAntTheme } from "hooks/useAntTheme";
import type { TeacherProfile } from "pages/creator-profile/types";
import { ProfileSectionCard } from "./ProfileSectionCard";
import { CreatorDnaModal } from "./CreatorDnaModal";
import type { CreatorDnaModalMode } from "./CreatorDnaModal";
import {
  getDnaArtwork,
  getDnaCopyKey,
  getDnaTraits,
} from "./creatorDnaArchetypes";

type CreatorDnaCardProps = Readonly<{
  id: string;
  /** Undefined while the profile is still resolving. */
  profile?: TeacherProfile;
}>;

export const CreatorDnaCard = ({ id, profile }: CreatorDnaCardProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [modalMode, setModalMode] = useState<CreatorDnaModalMode | null>(null);
  const archetypeName = profile?.archetype?.name;
  const artwork = archetypeName ? getDnaArtwork(archetypeName) : undefined;
  const traits = archetypeName ? getDnaTraits(archetypeName) : [];
  // Fall back to the name the API sent when we don't recognize the archetype,
  // rather than rendering a raw translation key.
  const isKnown = !!artwork && traits.length > 0;

  const renderBody = () => {
    if (!archetypeName) {
      return (
        <StyledPanelContainer>
          <StyledPanel $token={theme}>
            <StyledHeading vertical gap={8}>
              <Skeleton variant="rectangular" width={72} height={12} />
              <Skeleton variant="rectangular" width={260} height={32} />
            </StyledHeading>

            <StyledSkeletonArea $area="description">
              <Skeleton variant="text" rows={2} />
            </StyledSkeletonArea>

            <StyledSkeletonArtwork $token={theme}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                borderRadius={radius.box}
              />
            </StyledSkeletonArtwork>

            <StyledTraits gap={12}>
              {[0, 1, 2].map((slot) => (
                <Flex key={slot} vertical align="center" gap={6}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="rectangular" width={56} height={10} />
                </Flex>
              ))}
            </StyledTraits>

            <StyledActions gap={12} wrap>
              <Skeleton variant="rectangular" width={132} height={40} />
              <Skeleton variant="rectangular" width={132} height={40} />
            </StyledActions>
          </StyledPanel>
        </StyledPanelContainer>
      );
    }

    return (
      <StyledPanelContainer>
        <StyledPanel $token={theme}>
          <StyledHeading vertical gap={8}>
            <Eyebrow size="s">
              {t("pages.user.edit.creatorDna.eyebrow")}
            </Eyebrow>

            <Typography.Title level={2} style={{ fontWeight: 700, margin: 0 }}>
              {isKnown
                ? t(getDnaCopyKey(archetypeName, "name"))
                : archetypeName}
            </Typography.Title>
          </StyledHeading>

          {isKnown ? (
            <StyledDescription type="secondary">
              {t(getDnaCopyKey(archetypeName, "description"))}
            </StyledDescription>
          ) : null}

          {artwork ? <StyledArtwork src={artwork} alt={archetypeName} /> : null}

          {traits.length > 0 ? (
            <StyledTraits gap={12} wrap>
              {traits.map(({ key, icon: TraitIcon }) => (
                <Flex key={key} vertical align="center" gap={6}>
                  <StyledTraitIcon
                    $token={theme}
                    align="center"
                    justify="center"
                  >
                    <TraitIcon size={20} />
                  </StyledTraitIcon>
                  <StyledTraitLabel $token={theme}>
                    {t(`pages.user.edit.creatorDna.traits.${key}`)}
                  </StyledTraitLabel>
                </Flex>
              ))}
            </StyledTraits>
          ) : null}
          <StyledActions gap={12} wrap>
            <Button
              variant="outlined"
              rightIcon={<ArrowRightIcon />}
              onClick={() => setModalMode("view")}
            >
              {t("pages.user.edit.creatorDna.viewDetails")}
            </Button>
            <Button variant="primary" onClick={() => setModalMode("edit")}>
              {t("pages.user.edit.creatorDna.edit")}
            </Button>
          </StyledActions>
        </StyledPanel>
      </StyledPanelContainer>
    );
  };

  return (
    <ProfileSectionCard
      id={id}
      title={t("pages.user.edit.nav.creatorDna")}
      subtitle={t("pages.user.edit.creatorDna.subtitle")}
    >
      {renderBody()}
      <CreatorDnaModal
        mode={modalMode}
        profile={profile}
        onModeChange={setModalMode}
        onClose={() => setModalMode(null)}
      />
    </ProfileSectionCard>
  );
};

// ─── Styled components ───

const ARTWORK_COLUMN = "38%";

// Queried on the panel's own width: the side nav and the content column's
// max-width leave this panel narrow even on a wide viewport.
const StyledPanelContainer = styled.div`
  container-type: inline-size;
`;

// The empty rows above and below split the artwork's extra height, so the text
// stays packed together and vertically centred.
const StyledPanel = styled.div<{ $token: GlobalToken }>`
  display: grid;
  grid-template:
    ". artwork" 1fr
    "heading artwork" auto
    "description artwork" auto
    "traits artwork" auto
    "actions artwork" auto
    ". artwork" 1fr
    / minmax(0, 1fr) ${ARTWORK_COLUMN};
  gap: ${({ $token }) => `${$token.marginLG}px ${$token.margin}px`};
  padding: ${({ $token }) => $token.paddingLG}px;
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorFillQuaternary} url(${bkgNoise})
    center / cover no-repeat;

  @container (width <= 440px) {
    grid-template:
      "heading heading" auto
      "description artwork" auto
      "traits traits" auto
      "actions actions" auto
      / minmax(0, 1fr) auto;
  }
`;

const StyledSkeletonArea = styled.div<{ $area: string }>`
  grid-area: ${({ $area }) => $area};
`;

const StyledSkeletonArtwork = styled.div<{ $token: GlobalToken }>`
  grid-area: artwork;
  align-self: center;
  width: 100%;
  aspect-ratio: 3 / 4;

  @container (width <= 440px) {
    width: 100px;
  }
`;

const StyledHeading = styled(Flex)`
  grid-area: heading;
`;

const StyledDescription = styled(Typography.Text)`
  grid-area: description;
`;

const StyledArtwork = styled.img`
  grid-area: artwork;
  align-self: center;
  width: 100%;
  height: auto;
  border-radius: ${radius.box}px;

  @container (width <= 440px) {
    width: 100px;
  }
`;

const StyledTraits = styled(Flex)`
  grid-area: traits;
`;

const StyledActions = styled(Flex)`
  grid-area: actions;
`;

const StyledTraitIcon = styled(Flex)<{ $token: GlobalToken }>`
  width: 40px;
  height: 40px;
  border-radius: ${radius.circle};
  background: ${({ $token }) => $token.colorBgContainer};
  color: ${({ $token }) => $token.colorPrimary};
`;

const StyledTraitLabel = styled.span<{ $token: GlobalToken }>`
  max-width: 72px;
  color: ${({ $token }) => $token.colorTextSecondary};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  text-align: center;
`;
